import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { SessionAuthService } from './core/auth/session-auth.service';
import { LOCAL_STORAGE_KEYS } from './core/constants/local-storage.keys';
import { LocalStorageService } from './core/services';
import { ViewDefinitionResponse, ViewDefinitionService } from './features/workbench/services/view-definition.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  readonly code = environment.app.indexAppCd;
  /** ルーター描画を開始できるかどうか */
  isReady = false;

  /** 起動中に表示する説明メッセージ */
  bootstrapMessage = 'セッション情報を確認しています...';

  /** 起動中メッセージがエラーかどうか */
  bootstrapMessageIsError = false;

  constructor(
    private readonly sessionAuth: SessionAuthService,
    private readonly viewDefinitions: ViewDefinitionService,
    private readonly storage: LocalStorageService,
  ) {}

  /** session_key の検証完了後に標準フローへ進める */
  async ngOnInit(): Promise<void> {
    this.sessionAuth.bootstrapState$.subscribe(state => {
      this.bootstrapMessage = state.message;
      this.bootstrapMessageIsError = state.isError;
    });

    try {
      await this.initializeBizFrontendFeatures();
      await this.sessionAuth.initialize(this.code);
      this.resetWorkbenchFilterStateOnBootstrap();
      this.isReady = true;
    } catch {
      this.isReady = false;
    }
  }

  /** フルリロード時は ALL タブの永続フィルターを初期化し、別 session の条件を持ち越さない */
  private resetWorkbenchFilterStateOnBootstrap(): void {
    this.storage.setItem(LOCAL_STORAGE_KEYS.FILTER_CONFIG, []);
  }

  /**
   * BizFrontend ライブラリの初期化と View 定義取得を実行する
   * initializeApp は SessionAuthService 側で一度だけ実行される
   */
  private async initializeBizFrontendFeatures(): Promise<void> {
    const initResult = await this.sessionAuth.initializeApplication(this.code);
    if (!initResult) {
      return;
    }

    await this.loadViewDefinitions();
  }

  /** BizFrontend 初期化後に 4 つの View 定義を並列取得してキャッシュする */
  private async loadViewDefinitions(): Promise<void> {
    if (this.viewDefinitions.shouldUseLocalDefinitions()) {
      console.warn(
        '[DEV ONLY] Loading view definitions from local mock JSON files. Remove definitionSource=local from the URL to switch back to backend definitions.',
      );
      console.info(this.viewDefinitions.getLocalDefinitionUsageHint());
      this.viewDefinitions.replaceDefinitions(await this.viewDefinitions.loadLocalDefinitions());
      return;
    }

    const loadedDefinitions = await Promise.all(
      this.viewDefinitions.getFrontendDefinitionTargets().map(async target => ({
        viewCd: target.viewCd,
        definition: await this.sessionAuth.getFrontendDefinition<ViewDefinitionResponse>(target.frontendCode),
      })),
    );

    this.viewDefinitions.replaceDefinitions(
      new Map(loadedDefinitions.map(({ viewCd, definition }) => [viewCd, definition])),
    );
  }
}
